import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { UserCell, UserCellSkeleton, UserCellWithTooltip } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import type { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { type FieldValueChange } from "@/typings/formik"
import { type FieldInputProps } from "formik"
import SearchEmpty from "@images/empty/session_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { ExploreProfileItemEntity } from "@modules/explore/entities/exploreEntity"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { useExploreSearchContents } from "@modules/explore/hooks/useExplore"
import useDebounce from "@modules/hooks/useDebounce"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { LoaderCircle, PencilIcon, Search, X, XIcon } from "lucide-react"
import { useCallback, useEffect, useEffectEvent, useImperativeHandle, useRef, useState } from "react"

type SearchUserInputProps<K> = {
  label?: string
  name: string
  buttonTextSearch?: string
  textDescription?: string
  searchDescriptionText?: string
  isRequiredInput: boolean
  setFieldValue: FieldValueChange<K>
  user?: UserEntity | null
  classNameDiv?: string
  getFieldProps?: (nameOrOptions: string) => FieldInputProps<unknown>
  error?: React.JSX.Element
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, "children">
  tooltipTriggerProps?: Omit<React.ComponentProps<typeof TooltipTrigger>, "children" | "asChild">
  tooltipContentProps?: Omit<React.ComponentProps<typeof TooltipContent>, "children">
} & React.ComponentProps<"div">

export type SearchUserModalRef = {
  open: () => void
}

export function SearchUserInput<K>({
  label,
  name,
  buttonTextSearch,
  isRequiredInput,
  setFieldValue,
  getFieldProps,
  textDescription,
  searchDescriptionText,
  error,
  user,
  classNameDiv,
  tooltipProps,
  tooltipTriggerProps,
  tooltipContentProps,
  ...props
}: SearchUserInputProps<K>): React.JSX.Element {
  const searchUserModal = useRef<SearchUserModalRef>(null)
  const [userSelected, setUserSelected] = useState<ExploreProfileItemEntity | UserEntity | null>(user ?? null)

  const handleTrash = () => {
    setUserSelected(null)
    void setFieldValue(name, null)
  }

  const handleEdit = () => {
    searchUserModal.current?.open()
  }

  useEffect(() => {
    if (userSelected) {
      void setFieldValue(name, userSelected.id)
    }
  }, [name, setFieldValue, userSelected])

  const resetUserSelected = useEffectEvent(() => {
    setUserSelected(null)
    void setFieldValue(name, null)
  })

  useEffect(() => {
    if (getFieldProps?.(name).value === null) {
      resetUserSelected()
    }
  }, [getFieldProps, name])

  return (
    <>
      <InputContainer isRequiredInput={isRequiredInput} label={label} name={name} error={error} {...props}>
        <div className={cn("flex flex-col gap-3 rounded-xl bg-neutral-50 p-4", classNameDiv)}>
          {textDescription && (
            <Text fontStyle="BM" className="text-textDefault-body">
              {textDescription}
            </Text>
          )}
          {userSelected ? (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1 overflow-hidden">
                <UserCellWithTooltip
                  tooltipProps={tooltipProps}
                  tooltipTriggerProps={tooltipTriggerProps}
                  tooltipContentProps={tooltipContentProps}
                  member={{
                    avatar: userSelected.avatar ?? undefined,
                    firstname: userSelected.firstname,
                    lastname: userSelected.lastname,
                  }}
                  subtitle={userSelected.username === "" ? "" : "@" + userSelected.username}
                />
              </div>
              <div className="flex shrink-0 items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="iconMd"
                  onClick={() => {
                    handleEdit()
                  }}
                >
                  <PencilIcon />
                </Button>
                <Button
                  variant="secondary"
                  size="iconMd"
                  onClick={() => {
                    handleTrash()
                  }}
                >
                  <XIcon />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="lg"
              className="grow justify-start text-textDefault-placeholder"
              onClick={() => searchUserModal.current?.open()}
            >
              <Search size={16} color="currentColor" className="text-textDefault-body" />
              {buttonTextSearch ?? word("searchUserInput.modal.searchText")}
            </Button>
          )}
        </div>
      </InputContainer>
      <SearchUserModal
        ref={searchUserModal}
        setUserSelected={setUserSelected}
        searchDescriptionText={searchDescriptionText}
      />
    </>
  )
}

export function SearchUserModal({
  setUserSelected,
  searchDescriptionText,
  requestConfirmation = false,
  confirmText,
  cancelText,
  confirmDescriptionText,
  ref,
}: {
  setUserSelected: (user: ExploreProfileItemEntity) => void
  searchDescriptionText?: string
  requestConfirmation?: boolean
  confirmText?: string
  cancelText?: string
  confirmDescriptionText?: string
  ref: React.Ref<SearchUserModalRef>
}) {
  const [userSelected, setUserSelectedState] = useState<ExploreProfileItemEntity | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const {
    data: { content: result },
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
  } = useExploreSearchContents(ExploreFilter.PROFILE, debouncedSearchTerm)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  const onConfirm = useCallback(() => {
    if (userSelected) {
      setUserSelected(userSelected)
      setShowModal(false)
    }
  }, [userSelected, setUserSelected])

  const clearSearch = () => {
    setSearchTerm("")
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.currentTarget.value)
  }

  const handleClick = (profile: ExploreProfileItemEntity) => {
    // If confirmation is requested, set the user selected state without closing the modal
    if (requestConfirmation) {
      setUserSelectedState(profile)
      setSearchTerm("")
      return
    }
    // If no confirmation is requested, directly set the user selected
    setUserSelected(profile)
    setShowModal(false)
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className="h-135 w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center pt-4">
            <Text fontStyle="TL" className="text-textDefault-heading">
              {word("searchUserInput.modal.title")}
            </Text>
          </DialogTitle>
        </DialogHeader>

        <div className="flex grow flex-col">
          <div className="mt-5 flex w-full">
            <div
              className="flex h-12 w-full justify-between rounded-full border border-borderDefault"
              onFocus={(e) => {
                e.currentTarget.classList.remove("border-borderDefault")
                e.currentTarget.classList.add("border-borderPrimary")
              }}
              onBlur={(e) => {
                e.currentTarget.classList.remove("border-borderPrimary")
                e.currentTarget.classList.add("border-borderDefault")
              }}
            >
              <div className="flex grow items-center">
                <InputGroup>
                  <InputGroupInput
                    placeholder={word("searchUserInput.modal.placeholder")}
                    onChange={handleSearchChange}
                    value={searchTerm}
                  />
                  <InputGroupAddon align="inline-start">
                    <Search />
                  </InputGroupAddon>
                  {searchTerm && (
                    <InputGroupAddon align="inline-end">
                      <X className="size-4 cursor-pointer" onClick={clearSearch} />
                    </InputGroupAddon>
                  )}
                </InputGroup>
              </div>
            </div>
          </div>
          {/* SEARCH RESULTS */}
          <div className="mt-4 flex h-full w-full flex-col">
            <div className="flex h-85 flex-col overflow-y-auto">
              {userSelected && requestConfirmation ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 px-3">
                  {confirmDescriptionText && (
                    <Text fontStyle="BM" className="text-textDefault-body">
                      {confirmDescriptionText}
                    </Text>
                  )}

                  <div className="flex w-full flex-row items-center justify-between px-4">
                    <UserCell
                      member={{
                        avatar: userSelected.avatar,
                        firstname: userSelected.firstname,
                        lastname: userSelected.lastname,
                      }}
                      subtitle={userSelected.username === "" ? "" : "@" + userSelected.username}
                      className="flex gap-2"
                    />
                    <div
                      className="mr-3 flex cursor-pointer items-center p-2"
                      onClick={() => {
                        setUserSelectedState(null)
                      }}
                    >
                      <X className="size-4 cursor-pointer" color="currentColor" />
                    </div>
                  </div>
                </div>
              ) : isLoading && searchTerm !== "" ? (
                <div className="flex h-full w-full flex-col items-center justify-center">
                  <LoaderCircle className="animate-spin" />
                </div>
              ) : searchTerm !== "" && result.length > 0 ? (
                <div className="mt-4 flex flex-col gap-2">
                  {result.map(
                    (person) =>
                      person.profile && (
                        <UserCell
                          key={person.profile.id}
                          member={{
                            avatar: person.profile.avatar,
                            username: person.profile.username,
                            firstname: person.profile.firstname,
                            lastname: person.profile.lastname,
                          }}
                          subtitle={person.profile.username === "" ? "" : "@" + person.profile.username}
                          className="flex cursor-pointer gap-2"
                          onClick={() => {
                            if (person.profile) {
                              handleClick(person.profile)
                            }
                          }}
                        />
                      ),
                  )}
                  {(hasNextPage || isFetchingNextPage) && <TemplateUserCellSkeleton ref={loadMoreRef} />}
                </div>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-4">
                  <Text fontStyle="LL" className="text-textDefault-body">
                    {searchTerm !== "" ? word("searchUserInput.modal.emptyViewtext") : searchDescriptionText}
                  </Text>
                  <img src={SearchEmpty} alt="" className="h-40 w-full object-contain object-center" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        {requestConfirmation && (
          <div className="mt-4 flex w-full items-end gap-3">
            <Button variant="outline" onClick={onHide} className="grow">
              {cancelText}
            </Button>
            <Button variant="primary" onClick={onConfirm} className="grow" disabled={!userSelected}>
              {confirmText}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

type TemplateUserCellSkeletonProps = {
  className?: string
} & React.ComponentProps<"div">

function TemplateUserCellSkeleton({ className, ref, ...props }: TemplateUserCellSkeletonProps) {
  return (
    <div ref={ref} className={cn("flex flex-col gap-2", className)} {...props}>
      <UserCellSkeleton subtitle={true} />
      <UserCellSkeleton subtitle={true} />
      <UserCellSkeleton subtitle={true} />
    </div>
  )
}
